import { useState, useEffect, useRef } from 'react'
import { Button } from '../ui/Button'
import { Card } from '../ui/Card'
import { Input } from '../ui/Input'
import { Badge } from '../ui/Badge'
import { DiscountModal } from './DiscountModal'
import { PriceOverrideModal } from './PriceOverrideModal'
import { SaleCompletionOverlay } from './SaleCompletionOverlay'
import { CustomerSelector } from './CustomerSelector'
import { ShoppingCart, Trash2, Minus, Plus, Package, Percent, DollarSign, Edit, Tag } from 'lucide-react'

interface CartItem {
  id: string
  name: string
  originalPrice: number
  price: number
  quantity: number
  total: number
  gst_rate: number
  gst_type: 'inclusive' | 'exclusive'
  discount?: {
    type: 'fixed' | 'percentage'
    value: number
    amount: number
  }
  isWeighable?: boolean
  unit?: string
}

interface Customer {
  id: string
  name: string
  phone?: string
  email?: string
  gst_number?: string
}

interface SidebarCartProps {
  cart: CartItem[]
  customers: Customer[]
  selectedCustomer: Customer | null
  onUpdateQuantity: (productId: string, quantity: number) => void
  onRemoveFromCart: (productId: string) => void
  onClearCart: () => void
  onCustomerChange: (customerId: string) => void
  onAddCustomer: () => void
  formatCurrency: (amount: number) => string
  cartTotal: number
  highlightedItems?: Set<string>
  // Payment props
  paymentMethod: 'cash' | 'card' | 'upi' | 'split'
  setPaymentMethod: (method: 'cash' | 'card' | 'upi' | 'split') => void
  amountReceived: string
  setAmountReceived: (amount: string) => void
  splitPayments: { cash: string; card: string; upi: string }
  setSplitPayments: (payments: { cash: string; card: string; upi: string }) => void
  splitTotal: number
  onPreviewBill: () => void
  onProcessSale: () => void
  // New props for advanced features
  cartSubtotal: number
  orderDiscount?: { type: 'fixed' | 'percentage'; value: number } | null
  orderDiscountAmount: number
  taxRate: number
  taxAmount: number
  finalTotal: number
  onApplyItemDiscount?: (itemId: string, type: 'fixed' | 'percentage', value: number) => void
  onRemoveItemDiscount?: (itemId: string) => void
  onOverridePrice?: (itemId: string, price: number) => void
  onApplyOrderDiscount?: (type: 'fixed' | 'percentage', value: number) => void
  onRemoveOrderDiscount?: () => void
  // Sale completion props
  saleCompletionData?: {
    isVisible: boolean
    billNumber: string
    customer?: Customer | null
    totalAmount: number
    change: number
  } | null
  onPrintReceipt?: () => Promise<void>
  onSkipPrint?: () => void
  onNewSale?: () => void
  hideCartItems?: boolean
  hideSaleCompletion?: boolean
}

export const SidebarCart = ({
  cart,
  customers,
  selectedCustomer,
  onUpdateQuantity,
  onRemoveFromCart,
  onClearCart,
  onCustomerChange,
  onAddCustomer,
  formatCurrency,
  cartTotal,
  highlightedItems = new Set(),
  paymentMethod,
  setPaymentMethod,
  amountReceived,
  setAmountReceived,
  splitPayments,
  setSplitPayments,
  splitTotal,
  onPreviewBill,
  onProcessSale,
  // New props
  cartSubtotal,
  orderDiscount,
  orderDiscountAmount,
  taxRate,
  taxAmount,
  finalTotal,
  onApplyItemDiscount,
  onRemoveItemDiscount,
  onOverridePrice,
  onApplyOrderDiscount,
  onRemoveOrderDiscount,
  // Sale completion props
  saleCompletionData,
  onPrintReceipt,
  onSkipPrint,
  onNewSale,
  hideCartItems = false,
  hideSaleCompletion = false
}: SidebarCartProps) => {
  const [showItemDiscountModal, setShowItemDiscountModal] = useState<string | null>(null)
  const [showPriceOverrideModal, setShowPriceOverrideModal] = useState<string | null>(null)
  const [showOrderDiscountModal, setShowOrderDiscountModal] = useState(false)
  const [showPaymentSection, setShowPaymentSection] = useState(false)
  
  // Refs for auto-scrolling
  const cartScrollContainerRef = useRef<HTMLDivElement>(null)
  const itemRefs = useRef<{ [key: string]: HTMLDivElement | null }>({})
  
  // Reset payment section on new sale (when cart goes from empty to having items)
  useEffect(() => {
    if (cart.length === 0) {
      setShowPaymentSection(false)
    }
  }, [cart.length])
  
  // Auto-scroll to highlighted items
  useEffect(() => {
    if (highlightedItems.size > 0 && cartScrollContainerRef.current) {
      // Get the first highlighted item
      const highlightedItemId = Array.from(highlightedItems)[0]
      const itemElement = itemRefs.current[highlightedItemId]
      
      if (itemElement) {
        // Smooth scroll to the highlighted item with some padding
        itemElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'nearest'
        })
      }
    }
  }, [highlightedItems])
  
  const currentItemForDiscount = showItemDiscountModal ? cart.find(item => item.id === showItemDiscountModal) : null
  const currentItemForPriceOverride = showPriceOverrideModal ? cart.find(item => item.id === showPriceOverrideModal) : null
  return (
    <div className="w-full lg:w-96 bg-white dark:bg-gray-800 lg:border-l border-gray-200 dark:border-gray-700 flex flex-col transition-colors relative h-full">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100 flex items-center gap-2">
              <ShoppingCart className="h-5 w-5" />
              Cart
            </h2>
            {/* Compact Item/Qty Display */}
            <div className="flex items-center gap-3 text-sm">
              <div className="bg-orange-100 dark:bg-orange-900/30 px-2 py-1 rounded-md">
                <span className="text-orange-700 dark:text-orange-300 font-semibold">
                  {cart.length}
                </span>
                <span className="text-gray-600 dark:text-gray-400 ml-1">
                  {cart.length === 1 ? 'item' : 'items'}
                </span>
              </div>
              <div className="bg-blue-100 dark:bg-blue-900/30 px-2 py-1 rounded-md">
                <span className="text-blue-700 dark:text-blue-300 font-semibold">
                  {cart.reduce((sum, item) => sum + item.quantity, 0)}
                </span>
                <span className="text-gray-600 dark:text-gray-400 ml-1">qty</span>
              </div>
            </div>
          </div>
          {cart.length > 0 && (
            <Button variant="outline" size="sm" onClick={onClearCart}>
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>

      </div>

      {/* Cart Items */}
      {!hideCartItems && (
        <div ref={cartScrollContainerRef} className="flex-1 overflow-auto">
        {cart.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full p-6 text-center">
            <Package className="h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-800 mb-2">Cart is Empty</h3>
            <p className="text-gray-600 text-sm">Add products from the grid to get started</p>
          </div>
        ) : (
          <div className="p-2 space-y-1">
            {cart.map((item) => {
              const isHighlighted = highlightedItems.has(item.id)
              return (
              <div 
                key={item.id}
                ref={(el) => { itemRefs.current[item.id] = el }}
                className={`rounded-md p-2 border transition-all duration-500 ${
                  isHighlighted 
                    ? 'bg-orange-100 dark:bg-orange-900/30 border-orange-300 dark:border-orange-600 shadow-lg scale-105' 
                    : 'bg-gray-50 dark:bg-gray-700/50 border-gray-200 dark:border-gray-600'
                }`}
              >
                {/* Compact Item Row */}
                <div className="flex items-center justify-between mb-1">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1">
                      <h4 className="font-medium text-xs text-gray-900 dark:text-gray-100 truncate" title={item.name}>
                        {item.name}
                      </h4>
                      {item.isWeighable && (
                        <Badge variant="secondary" size="sm" className="text-xs px-1 py-0">
                          {item.unit}
                        </Badge>
                      )}
                      {item.price !== item.originalPrice && (
                        <Badge variant="warning" size="sm" className="text-xs px-1 py-0">
                          Override
                        </Badge>
                      )}
                      {item.discount && (
                        <Badge variant="success" size="sm" className="text-xs px-1 py-0">
                          {item.discount.type === 'percentage' ? `${item.discount.value}%` : `₹${item.discount.value}`}
                        </Badge>
                      )}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {formatCurrency(item.originalPrice)} each - GST: {item.gst_rate}% {item.gst_type === 'inclusive' ? 'inc' : 'exc'}
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="text-right">
                      <div className="font-semibold text-sm text-orange-600 dark:text-orange-400">
                        {formatCurrency(item.total)}
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="xs"
                      onClick={() => onRemoveFromCart(item.id)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 h-5 w-5 p-0"
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </div>

                {/* Compact Quantity & Actions Row */}
                <div className="flex items-center justify-between gap-2">
                  {/* Quantity Controls */}
                  <div className="flex items-center gap-1">
                    <span className="text-xs text-gray-600 dark:text-gray-400 mr-1">Qty:</span>
                    <Button
                      variant="outline"
                      size="xs"
                      onClick={() => {
                        const decrement = item.isWeighable ? 0.1 : 1
                        const minQty = item.isWeighable ? 0.1 : 1
                        onUpdateQuantity(item.id, Math.max(minQty, item.quantity - decrement))
                      }}
                      className="h-5 w-5 p-0"
                    >
                      <Minus className="h-2 w-2" />
                    </Button>
                    <div className="min-w-[45px] text-center">
                      <Input
                        type="number"
                        min={item.isWeighable ? "0.01" : "1"}
                        step={item.isWeighable ? "0.1" : "1"}
                        value={item.quantity}
                        onChange={(e) => {
                          const qty = parseFloat(e.target.value) || (item.isWeighable ? 0.1 : 1)
                          const minQty = item.isWeighable ? 0.01 : 1
                          onUpdateQuantity(item.id, Math.max(minQty, qty))
                        }}
                        className="w-full text-center h-5 text-xs font-medium px-1"
                      />
                    </div>
                    <Button
                      variant="outline"
                      size="xs"
                      onClick={() => {
                        const increment = item.isWeighable ? 0.1 : 1
                        onUpdateQuantity(item.id, item.quantity + increment)
                      }}
                      className="h-5 w-5 p-0"
                    >
                      <Plus className="h-2 w-2" />
                    </Button>
                  </div>
                  
                  {/* Action Buttons */}
                  <div className="flex items-center gap-1">
                    {item.discount ? (
                      <Button
                        variant="ghost"
                        size="xs"
                        onClick={() => onRemoveItemDiscount?.(item.id)}
                        className="text-xs text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 h-5 px-2 min-w-0"
                        title="Remove Discount"
                      >
                        <Percent className="h-2 w-2 mr-1" />
                        <span className="text-xs">Remove</span>
                      </Button>
                    ) : (
                      <Button
                        variant="ghost"
                        size="xs"
                        onClick={() => setShowItemDiscountModal(item.id)}
                        className="text-xs text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 h-5 px-2 min-w-0"
                        title="Apply Discount"
                      >
                        <Percent className="h-2 w-2 mr-1" />
                        <span className="text-xs">Disc</span>
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="xs"
                      onClick={() => setShowPriceOverrideModal(item.id)}
                      className="text-xs text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 h-5 px-2 min-w-0"
                      title="Override Price"
                    >
                      <Edit className="h-2 w-2 mr-1" />
                      <span className="text-xs">Price</span>
                    </Button>
                  </div>
                </div>
              </div>
              )
            })}
          </div>
        )}
        </div>
      )}

      {/* Checkout Section */}
      {cart.length > 0 && (
        <div className={`border-t border-gray-200 dark:border-gray-700 p-3 bg-gray-50 dark:bg-gray-700/50 space-y-2 ${hideCartItems ? 'flex-1 overflow-auto' : ''}`}>
          {/* Order Summary */}
          <div className="space-y-1">
            <div className="flex justify-between text-xs">
              <span>Subtotal:</span>
              <span>{formatCurrency(cartSubtotal)}</span>
            </div>
            
            {/* Order Discount */}
            {orderDiscount && (
              <div className="flex justify-between text-xs text-green-600 dark:text-green-400">
                <span>Discount ({orderDiscount.type === 'percentage' ? `${orderDiscount.value}%` : formatCurrency(orderDiscount.value)}):</span>
                <span>-{formatCurrency(orderDiscountAmount)}</span>
              </div>
            )}
            
            {/* Simplified Tax Display */}
            <div className="flex justify-between text-xs">
              <span>Tax (inclusive):</span>
              <span>+{formatCurrency(taxAmount || 0)}</span>
            </div>
            
            {/* Final Total */}
            <div className="flex justify-between items-center text-base font-semibold border-t border-gray-200 dark:border-gray-600 pt-1 text-gray-900 dark:text-gray-100">
              <span>Total:</span>
              <span className="text-orange-600 dark:text-orange-400">{formatCurrency(finalTotal)}</span>
            </div>
            
            {/* Order Discount Controls */}
            <div className="flex gap-1 pt-1">
              <Button
                variant="ghost"
                size="xs"
                onClick={() => setShowOrderDiscountModal(true)}
                className="flex-1 text-xs text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 h-6"
              >
                <Percent className="h-2 w-2 mr-1" />
                Order Discount
              </Button>
              {orderDiscount && (
                <Button
                  variant="ghost"
                  size="xs"
                  onClick={() => onRemoveOrderDiscount?.()}
                  className="text-xs text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 h-6 px-2"
                >
                  Remove
                </Button>
              )}
            </div>
          </div>

          {/* Payment Method - Only shown when showPaymentSection is true */}
          {showPaymentSection && (
            <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-lg border border-yellow-200 dark:border-yellow-700/50 space-y-3">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-base font-semibold text-yellow-800 dark:text-yellow-200">Complete Payment</h4>
                <Button
                  variant="ghost"
                  size="xs"
                  onClick={() => setShowPaymentSection(false)}
                  className="text-yellow-600 hover:text-yellow-700 hover:bg-yellow-100 dark:hover:bg-yellow-900/40 h-6 w-6 p-0"
                >
                  ×
                </Button>
              </div>

              {/* Customer Selection */}
              <div className="mb-3">
                <label className="block text-sm font-semibold text-yellow-800 dark:text-yellow-200 mb-2">Customer</label>
                <CustomerSelector
                  customers={customers}
                  selectedCustomer={selectedCustomer}
                  onCustomerChange={onCustomerChange}
                  onAddCustomer={onAddCustomer}
                  className="w-full"
                  hideLabel={true}
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-yellow-800 dark:text-yellow-200 mb-2">Payment Method</label>
                <div className="grid grid-cols-2 gap-2 mb-3">
                  {[
                    { key: 'cash', label: 'Cash' },
                    { key: 'card', label: 'Card' },
                    { key: 'upi', label: 'UPI' },
                    { key: 'split', label: 'Split' }
                  ].map(({ key, label }) => (
                    <Button
                      key={key}
                      variant={paymentMethod === key ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setPaymentMethod(key as any)}
                      className={`text-sm h-10 font-medium transition-all ${
                        paymentMethod === key 
                          ? 'bg-orange-600 hover:bg-orange-700 text-white shadow-lg scale-105' 
                          : 'border-yellow-300 dark:border-yellow-600 hover:bg-yellow-100 dark:hover:bg-yellow-800/30 hover:border-yellow-400 dark:hover:border-yellow-500'
                      }`}
                    >
                      {label}
                    </Button>
                  ))}
                </div>

                {/* Cash Payment */}
                {paymentMethod === 'cash' && (
                  <div>
                    <Input
                      type="number"
                      placeholder="Amount received"
                      value={amountReceived}
                      onChange={(e) => setAmountReceived(e.target.value)}
                      className="text-right text-sm h-10 bg-white dark:bg-gray-800 border-yellow-300 dark:border-yellow-600 focus:ring-yellow-500 focus:border-yellow-500"
                      size="sm"
                      autoFocus
                    />
                    {amountReceived && parseFloat(amountReceived) >= finalTotal && (
                      <div className="mt-2 p-2 bg-green-50 dark:bg-green-900/20 rounded text-sm">
                        <div className="flex justify-between font-semibold">
                          <span>Change:</span>
                          <span className="text-green-600 dark:text-green-400">
                            {formatCurrency(parseFloat(amountReceived) - finalTotal)}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Split Payment */}
                {paymentMethod === 'split' && (
                  <div className="space-y-2">
                    <div className="grid grid-cols-3 gap-1 text-xs">
                      <Input
                        type="number"
                        placeholder="Cash"
                        value={splitPayments.cash}
                        onChange={(e) => setSplitPayments({ ...splitPayments, cash: e.target.value })}
                        className="h-8 text-xs bg-white dark:bg-gray-800 border-yellow-300 dark:border-yellow-600"
                        size="sm"
                      />
                      <Input
                        type="number"
                        placeholder="Card"
                        value={splitPayments.card}
                        onChange={(e) => setSplitPayments({ ...splitPayments, card: e.target.value })}
                        className="h-8 text-xs bg-white dark:bg-gray-800 border-yellow-300 dark:border-yellow-600"
                        size="sm"
                      />
                      <Input
                        type="number"
                        placeholder="UPI"
                        value={splitPayments.upi}
                        onChange={(e) => setSplitPayments({ ...splitPayments, upi: e.target.value })}
                        className="h-8 text-xs bg-white dark:bg-gray-800 border-yellow-300 dark:border-yellow-600"
                        size="sm"
                      />
                    </div>
                    <div className="p-2 bg-gray-100 dark:bg-gray-600 rounded text-sm">
                      <div className="flex justify-between">
                        <span>Paid:</span>
                        <span className={splitTotal >= finalTotal ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}>
                          {formatCurrency(splitTotal)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span>Remaining:</span>
                        <span className="text-gray-600 dark:text-gray-400">
                          {formatCurrency(Math.max(0, finalTotal - splitTotal))}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-2">
            <Button
              onClick={onPreviewBill}
              variant="outline"
              size="sm"
              className="flex-1 text-xs h-8"
              title="Preview Bill (F2)"
            >
              Preview
              <span className="ml-1 text-xs opacity-60">F2</span>
            </Button>
            {!showPaymentSection ? (
              <Button
                onClick={() => setShowPaymentSection(true)}
                size="sm"
                className="flex-1 text-xs h-8 bg-orange-600 hover:bg-orange-700"
                title="Complete Sale (F3)"
              >
                Complete Sale
                <span className="ml-1 text-xs opacity-60">F3</span>
              </Button>
            ) : (
              <Button
                onClick={onProcessSale}
                size="sm"
                className="flex-1 text-sm h-10 bg-green-600 hover:bg-green-700 font-semibold"
                disabled={
                  (paymentMethod === 'cash' && (!amountReceived || parseFloat(amountReceived || '0') < finalTotal)) ||
                  (paymentMethod === 'split' && splitTotal < finalTotal)
                }
                title="Submit Payment"
              >
                Submit Payment
              </Button>
            )}
          </div>
        </div>
      )}
      
      {/* Modals */}
      <DiscountModal
        isOpen={showItemDiscountModal !== null}
        onClose={() => setShowItemDiscountModal(null)}
        onApply={(type, value) => {
          if (showItemDiscountModal && onApplyItemDiscount) {
            onApplyItemDiscount(showItemDiscountModal, type, value)
          }
          setShowItemDiscountModal(null)
        }}
        title={`Apply Discount - ${currentItemForDiscount?.name || ''}`}
        currentPrice={currentItemForDiscount ? currentItemForDiscount.price * currentItemForDiscount.quantity : undefined}
      />
      
      <PriceOverrideModal
        isOpen={showPriceOverrideModal !== null}
        onClose={() => setShowPriceOverrideModal(null)}
        onApply={(newPrice) => {
          if (showPriceOverrideModal && onOverridePrice) {
            onOverridePrice(showPriceOverrideModal, newPrice)
          }
          setShowPriceOverrideModal(null)
        }}
        itemName={currentItemForPriceOverride?.name || ''}
        originalPrice={currentItemForPriceOverride?.originalPrice || 0}
        currentPrice={currentItemForPriceOverride?.price || 0}
      />
      
      <DiscountModal
        isOpen={showOrderDiscountModal}
        onClose={() => setShowOrderDiscountModal(false)}
        onApply={(type, value) => {
          if (onApplyOrderDiscount) {
            onApplyOrderDiscount(type, value)
          }
          setShowOrderDiscountModal(false)
        }}
        title="Apply Order Discount"
        currentPrice={cartSubtotal}
      />

      {/* Sale Completion Overlay */}
      {saleCompletionData && !hideSaleCompletion && (
        <SaleCompletionOverlay
          isVisible={saleCompletionData.isVisible}
          saleData={{
            billNumber: saleCompletionData.billNumber,
            items: cart.map(item => ({
              id: item.id,
              name: item.name,
              price: item.price,
              quantity: item.quantity,
              total: item.total,
              unit: item.unit
            })),
            subtotal: cartSubtotal,
            taxAmount: taxAmount,
            totalAmount: finalTotal,
            paymentMethod,
            amountReceived: paymentMethod === 'cash' ? parseFloat(amountReceived || '0') : finalTotal,
            change: saleCompletionData.change,
            customer: saleCompletionData.customer
          }}
          onPrint={onPrintReceipt || (() => Promise.resolve())}
          onSkipPrint={onSkipPrint || (() => {})}
          onNewSale={onNewSale || (() => {})}
          formatCurrency={formatCurrency}
        />
      )}
    </div>
  )
}